import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Navbar from '@/components/navbar';
import Footer from '@/components/footer';
import CookieBanner from '@/components/cookie-banner';
import Home from '@/pages/home';
import Contact from '@/pages/contact';
import Register from '@/pages/register';
import VerifyOtp from '@/pages/verify-otp';
import ForgotPassword from '@/pages/forgot-password';
import ResetPassword from '@/pages/reset-password';
import Dashboard from '@/pages/dashboard';
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminPanel from '@/pages/admin/AdminPanel';
import Settings from '@/pages/settings';
import Plans from '@/pages/plans';
import About from '@/pages/about';
import PrivacyPolicy from '@/pages/privacy-policy';
import TermsOfService from '@/pages/terms-of-service';
import RefundPolicy from '@/pages/refund-policy';
import CopyrightPolicy from '@/pages/dmca-policy';
import AcceptableUsePolicy from '@/pages/acceptable-use-policy';
import CookiePolicy from '@/pages/cookie-policy';
import BetaTerms from '@/pages/beta-terms';
import ComingSoon from '@/pages/coming-soon';
import BlogIndex from '@/pages/blog/index';
import BlogPost from '@/pages/blog/article';
import Updates from '@/pages/updates';
import UpdateArticle from '@/pages/updates/Article';
import FAQ from '@/pages/faq';
import HowItWorks from '@/pages/how-it-works';
import BallSimulation from '@/pages/landing/BallSimulation';
import PhysicsVideoGenerator from '@/pages/landing/PhysicsVideoGenerator';
import ASMRVideoGenerator from '@/pages/landing/ASMRVideoGenerator';
import FacelessVideoGenerator from '@/pages/landing/FacelessVideoGenerator';
import YouTubeShortsGenerator from '@/pages/landing/YouTubeShortsGenerator';
import Showcase from '@/pages/Showcase';
import VsBlender from '@/pages/landing/VsBlender';
import VsAfterEffects from '@/pages/landing/VsAfterEffects';
import TutorialsHub from '@/pages/TutorialsHub';
import TutorialDetail from '@/pages/TutorialDetail';
import CaseStudies from '@/pages/CaseStudies';
import HelpLayout from '@/pages/help/HelpLayout';
import HelpIndex from '@/pages/help/HelpIndex';
import HelpDetail from '@/pages/help/HelpDetail';
import Login from '@/pages/login';
import SitemapPage from '@/pages/SitemapPage';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/context/auth-context';
import { PaymentProvider } from '@/contexts/PaymentContext';
import CreatorStudio from '@/studio/StudioApp';
import { Toaster } from '@/components/ui/sonner';
import UsernameSetupModal from '@/components/username-setup-modal';

// Scroll to top on route change
import { PWAProvider } from '@/context/pwa-context';
import PWAInstallPopup from '@/components/pwa-install-popup';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

import { getActiveRecipe, cn } from '@/lib/utils';

const AppLayout: React.FC<{ isFullscreen: boolean }> = ({ isFullscreen }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isStudio = location.pathname === '/studio';
  const isAdmin = location.pathname.startsWith('/admin');
  const isNoPaddingPage = [
    '/',
    '/login', 
    '/register', 
    '/signup', 
    '/verify-otp', 
    '/forgot-password', 
    '/reset-password', 
    '/dashboard', 
    '/settings', 
    '/plans',
    '/how-it-works',
    '/about',
    '/contact',
    '/faq',
    '/blog',
    '/updates',
    '/showcase',
    '/tutorials',
    '/case-studies',
    '/vs-blender',
    '/vs-after-effects',
    '/ball-simulation',
    '/physics-video-generator',
    '/asmr-video-generator',
    '/faceless-video-generator',
    '/youtube-shorts-generator',
    '/sitemap'
  ].includes(location.pathname) || location.pathname.startsWith('/tutorials/');
  const recipe = getActiveRecipe();
  
  return (
    <div className={cn(
      "flex flex-col min-h-screen",
      recipe.bg,
      recipe.text,
      (isFullscreen || isStudio || isAdmin) ? 'overflow-hidden h-screen' : 'overflow-x-hidden',
      "selection:bg-primary selection:text-primary-foreground transition-colors duration-500"
    )}>
      {!isFullscreen && !isStudio && !isAdmin && <Navbar />}
      <main className={cn(
        "flex-grow relative",
        (isFullscreen || isStudio || isAdmin || isNoPaddingPage) ? 'pt-0' : 'pt-28'
      )}>
        {/* Universal Ambient Overlay */}
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,rgba(var(--color-accent),0.02),transparent_70%)]" />
        
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-otp" element={<VerifyOtp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/plan" element={<Navigate to="/plans" replace />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/copyright-policy" element={<CopyrightPolicy />} />
          <Route path="/acceptable-use" element={<AcceptableUsePolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/beta-terms" element={<BetaTerms />} />
          <Route path="/coming-soon" element={<ComingSoon />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/updates" element={<Updates />} />
          <Route path="/updates/:slug" element={<UpdateArticle />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/showcase" element={<Showcase />} />
          <Route path="/tutorials" element={<TutorialsHub />} />
          <Route path="/tutorials/:slug" element={<TutorialDetail />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/vs-blender" element={<VsBlender />} />
          <Route path="/vs-after-effects" element={<VsAfterEffects />} />
          <Route path="/ball-simulation" element={<BallSimulation />} />
          <Route path="/physics-video-generator" element={<PhysicsVideoGenerator />} />
          <Route path="/asmr-video-generator" element={<ASMRVideoGenerator />} />
          <Route path="/faceless-video-generator" element={<FacelessVideoGenerator />} />
          <Route path="/youtube-shorts-generator" element={<YouTubeShortsGenerator />} />
          <Route path="/help" element={<HelpLayout />}>
            <Route index element={<HelpIndex />} />
            <Route path=":category/:slug" element={<HelpDetail />} />
          </Route>
          <Route path="/about" element={<About />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          {/* <Route path="/profile" element={<Profile />} />
          <Route path="/profile/:publicId" element={<Profile />} /> */}
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/panel" element={<AdminPanel />} />
          <Route path="/signup" element={<Register />} />
          <Route path="/studio" element={
            loading ? (
              <div className="h-screen w-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                  <p className="text-[10px] uppercase font-bold tracking-[0.3em] text-muted-foreground animate-pulse">Establishing Secure Session...</p>
                </div>
              </div>
            ) : user ? (
              <CreatorStudio />
            ) : (
              <Navigate to="/login?redirect=/studio" replace />
            )
          } />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
      {!isFullscreen && !isStudio && !isAdmin && <Footer />}

      {!isStudio && !isAdmin && <CookieBanner />}
      <UsernameSetupModal />
      <Toaster position="top-center" richColors />
      {!isStudio && !isAdmin && <PWAInstallPopup />}
    </div>
  );
};

const App: React.FC = () => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  React.useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      document.body.style.overflow = 'auto';
    };
  }, [isFullscreen]);

  return (
    <HelmetProvider>
      <AuthProvider>
        <PaymentProvider>
          <PWAProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AppLayout isFullscreen={isFullscreen} />
            </BrowserRouter>
          </PWAProvider>
        </PaymentProvider>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;